import type {
  FeatureItem,
  FeaturesIconStyle,
  FeaturesProps,
  FeaturesStyle,
} from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { ColorField, SelectField, TextField } from "./fields.js";
import type { WidgetInspectorProps } from "./registry.js";

const COLS = ["2", "3", "4"] as const;
const STYLE = ["card", "plain"] as const;
const ICON_STYLE = ["badge", "tint", "plain"] as const;

function newItemId(): string {
  return `ft-${Math.random().toString(36).slice(2, 8)}`;
}

export function FeaturesInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as FeaturesProps;
  const items = p.items ?? [];

  const setItems = (next: FeatureItem[]) => update(widget.id, { items: next });

  const addItem = () =>
    setItems([
      ...items,
      {
        id: newItemId(),
        icon: "✨",
        title: "New feature",
        description: "Description of the feature.",
      },
    ]);

  const updateItem = (id: string, patch: Partial<FeatureItem>) =>
    setItems(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const removeItem = (id: string) => setItems(items.filter((it) => it.id !== id));

  const moveItem = (id: string, dir: "up" | "down") => {
    const idx = items.findIndex((it) => it.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setItems(next);
  };

  return (
    <>
      <SelectField
        label="Columns"
        value={String(p.columns ?? 3) as (typeof COLS)[number]}
        options={COLS}
        onChange={(v) =>
          update(widget.id, {
            columns: Number.parseInt(v, 10) as FeaturesProps["columns"],
          })
        }
      />
      <SelectField
        label="Style"
        value={p.style ?? "card"}
        options={STYLE}
        onChange={(v) => update(widget.id, { style: v as FeaturesStyle })}
      />
      <SelectField
        label="Icon style"
        value={p.iconStyle ?? "badge"}
        options={ICON_STYLE}
        onChange={(v) => update(widget.id, { iconStyle: v as FeaturesIconStyle })}
      />
      <ColorField
        label="Accent"
        value={p.accent ?? "#111827"}
        onChange={(v) => update(widget.id, { accent: v })}
      />
      <ColorField
        label="Card background"
        value={p.cardBackground ?? "#ffffff"}
        onChange={(v) => update(widget.id, { cardBackground: v })}
      />
      <ColorField
        label="Text"
        value={p.textColor ?? "#0f172a"}
        onChange={(v) => update(widget.id, { textColor: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Features ({items.length})
      </div>

      {items.map((it, i) => (
        <div
          key={it.id}
          style={{
            padding: "8px 16px 10px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "up")}
              disabled={i === 0}
              title="Move up"
              style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
            >
              ↑
            </button>
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "down")}
              disabled={i === items.length - 1}
              title="Move down"
              style={{
                padding: "2px 6px",
                opacity: i === items.length - 1 ? 0.4 : 1,
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="emke-btn is-danger"
              onClick={() => removeItem(it.id)}
              title="Delete"
              style={{ padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>

          <div className="emke-inspector-grid-2" style={{ padding: "0" }}>
            <TextField
              label={`Icon ${i + 1}`}
              value={it.icon}
              onChange={(v) => updateItem(it.id, { icon: v })}
            />
            <TextField
              label="Title"
              value={it.title}
              onChange={(v) => updateItem(it.id, { title: v })}
            />
          </div>
          <TextField
            label="Description"
            value={it.description}
            multiline
            onChange={(v) => updateItem(it.id, { description: v })}
          />
        </div>
      ))}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addItem}
          style={{ width: "100%" }}
        >
          + Add feature
        </button>
      </div>
    </>
  );
}
